import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";

/**
 * ConfirmDialog — a reliable in-app confirmation modal for the webtop.
 *
 * Replaces window.confirm(), which is unreliable inside the Electron webtop
 * window: it can silently return false WITHOUT showing a dialog, which made
 * destructive actions (e.g. deleting a desktop file) appear to do nothing. This
 * always renders, so a confirm can never be silently swallowed.
 *
 * props: { title?, message, confirmLabel?, danger?, onConfirm, onCancel }
 */
const ConfirmDialog = ({
  title = "Are you sure?",
  message,
  confirmLabel = "Delete",
  danger = true,
  onConfirm,
  onCancel,
}) => {
  const confirmRef = useRef(null);

  useEffect(() => {
    if (confirmRef.current && typeof confirmRef.current.focus === "function") {
      confirmRef.current.focus();
    }
    const onKey = (e) => {
      if (e.key === "Escape") onCancel();
      else if (e.key === "Enter") onConfirm();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onConfirm, onCancel]);

  // Render to <body> via a portal so the modal escapes the webtop's nested
  // stacking contexts (e.g. the z-index:2 DesktopFiles layer) and always paints
  // above windows/taskbar instead of being trapped beneath them.
  const node = (
    <div
      className="webtop-confirm"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="webtop-confirm__box">
        <div className="webtop-confirm__title">{title}</div>
        <div className="webtop-confirm__msg">{message}</div>
        <div className="webtop-confirm__actions">
          <button type="button" className="webtop-confirm__btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            ref={confirmRef}
            className={`webtop-confirm__btn${danger ? " webtop-confirm__btn--danger" : ""}`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined" || !document.body) return node;
  return ReactDOM.createPortal(node, document.body);
};

export default ConfirmDialog;
